import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnDestroy, OnInit, signal, WritableSignal} from '@angular/core';
import { Subscription } from 'rxjs';
import {BUID, BUIDMid, MerchantService, OdinFeatures, UserOrganisation, UserOrganisationBusinessUnit, UserProfile, getActiveOrganisationId, getAvailableOrganisations, getBusinessUnitsForActiveOrg} from '@odin/odin-core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MerchantChangeService, ErrorReferenceService} from '@odin/odin-core';
import {CognitoService} from "@odin/odin-authentication";
import { Router } from '@angular/router';

interface LoadingBuid extends BUID {
  favouriteLoading: boolean;
}

@Component({
  standalone: false,
  selector: 'odin-merchant-picker',
  templateUrl: './merchant-picker.component.html',
  styleUrls: ['./merchant-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MerchantPickerComponent implements OnInit, OnDestroy {

  @Input() public dropUp: boolean = false;
  @Input() public showMids: boolean = true;

  public currentBuidId: string = this.merchantService.getMerchantRef();
  public saved_currentMid: BUIDMid = this.merchantService.getCurrentMid();

  public profileBuids: BUID[] = this.merchantChangeService.getMerchantRefs();
  public previewingBuids: BUID[] = [];

  public allBuids: () => BUID[] = () => [...this.profileBuids, ...this.previewingBuids];

  public filteredBuids: LoadingBuid[] = this.allBuids().sort((a: BUID, b: BUID) => Number(b.favourite) - Number(a.favourite)).map((m: BUID) => { return { ...m, favouriteLoading: false } as LoadingBuid; });
  public filter: string = '';
  public selectedBuid: WritableSignal<BUID | undefined> = signal<BUID | undefined>(undefined);
  public selectedMid: WritableSignal<BUIDMid | undefined> = signal<BUIDMid | undefined>(undefined);
  public dropdownActive: boolean = false;
  public fullscreen: boolean = false;

  public organisations: UserOrganisation[] = [];
  public activeOrganisationId: string = '';
  public filteredOtherOrgs: UserOrganisation[] = [];
  public switchingOrgId: WritableSignal<string | null> = signal<string | null>(null);
  private pendingBuidId: string | null = null;
  private openRequestSub: Subscription | null = null;

  public currentBuid(): BUID | null {
    if (this.currentBuidId === '') return null;
    return (
      this.allBuids().find((m) => m.id == this.currentBuidId) || null
    );
  }

  public currentMid(): BUIDMid | null {
    const _currentBuid = this.currentBuid();
    if (_currentBuid === null || _currentBuid === undefined || _currentBuid?.mids === undefined) return null;
    if (this.saved_currentMid === undefined || this.saved_currentMid.monekId === undefined) {
      return _currentBuid.mids.find((m: BUIDMid) => m.isPrimary && m.status.toUpperCase() === 'ACTIVE') || null;
    }
    return _currentBuid.mids.find((m) => m.monekId === this.saved_currentMid.monekId) || null;
  }

  constructor(
    private merchantChangeService: MerchantChangeService,
    private merchantService: MerchantService,
    private snackbar: MatSnackBar,
    private errorReferenceService: ErrorReferenceService,
    private cognitoService: CognitoService,
    private router: Router,
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    @Inject('features') private featureHelper: OdinFeatures,
  ) {
    this.showMids = this.showMids && this.featureHelper['mid-visibility'];

    this.merchantChangeService.addPreviewBuid.subscribe((previewBuid: BUID) => {
      previewBuid = JSON.parse(JSON.stringify({...previewBuid})) as BUID; // deep clone to avoid reference issues
      if (this.previewingBuids.findIndex(m => m.id === previewBuid.id) !== -1) return;
      previewBuid.name = `(PREVIEW) ${previewBuid.name}`;
      this.previewingBuids.push(previewBuid);
      this.snackbar.open(`Added to location options: ${previewBuid.name}`, 'Close', {
        duration: 1500,
      });
    });

    this.merchantChangeService.merchantChangeEvent.subscribe((_: { buid: string, mid: string }) => {
      this.currentBuidId = _.buid;
      setTimeout(() => {
        this.profileBuids = this.merchantChangeService.getMerchantRefs();
        this.saved_currentMid = this.merchantService.getCurrentMid();

        this.selectedBuid.set(this.currentBuid() || undefined)
        this.selectedMid.set(this.currentMid() || undefined);
        this.refreshData();
      }, 50);
    });

    // Pages can ask the picker to open (e.g. the reports overview's "Switch organisation"
    // button). Two instances exist (mobile side nav / desktop header); only the one
    // currently rendered responds so the hidden copy doesn't toggle the body class.
    this.openRequestSub = this.merchantChangeService.openPickerRequest.subscribe(({ fullscreen }) => {
      if (this.host.nativeElement.offsetParent === null) return;
      if (!this.dropdownActive) this.ToggleDropdown();
      this.setFullscreen(fullscreen);
      this.cdr.markForCheck();
    });

    this.cognitoService.profileChanged.subscribe((profile: UserProfile) => {
      if (this.switchingOrgId() !== null) {
        const targetBuidId = this.pendingBuidId;
        this.switchingOrgId.set(null);
        this.pendingBuidId = null;
        this.refreshData();
        const newOrgBuids = getBusinessUnitsForActiveOrg(profile);
        const target = (targetBuidId ? newOrgBuids.find(b => b.id === targetBuidId) : undefined) || newOrgBuids[0];
        // fetchProfile may already have fallen back to the org's first BUID; only
        // select (and emit a merchant change) when the target differs from it.
        if (target && target.id !== this.merchantService.getMerchantRef()) this.SelectMerchant(target);
        if (this.dropdownActive) this.ToggleDropdown();
        return;
      }
      this.refreshData();
    });
  }

  ngOnInit(): void {
    this.refreshData();
  }

  ngOnDestroy(): void {
    this.openRequestSub?.unsubscribe();
    document.body.classList.remove('merchant-picker-fullscreen');
  }

  private refreshData(): void {
    this.profileBuids = this.merchantChangeService.getMerchantRefs();
    this.currentBuidId = this.merchantService.getMerchantRef();
    this.saved_currentMid = this.merchantService.getCurrentMid();

    const profile = this.cognitoService.userProfile();
    this.organisations = getAvailableOrganisations(profile);
    this.activeOrganisationId = getActiveOrganisationId(profile);

    this.selectedBuid.set(this.currentBuid() || undefined)
    this.selectedMid.set(this.currentMid() || undefined)

    this.FilterBuids();
  }

  public onFilterChange(value: string): void {
    this.filter = value;
    this.FilterBuids();
  }

  public FilterBuids(): void {
    const sorted = [...this.allBuids()]
      .sort((a: BUID, b: BUID) => Number(b.favourite) - Number(a.favourite))
      .map((m: BUID) => ({ ...m, favouriteLoading: false }) as LoadingBuid);

    const otherOrgs = this.organisations.filter(o => o.organisationId !== this.activeOrganisationId);

    if (this.filter === '') {
      this.filteredBuids = sorted;
      this.filteredOtherOrgs = otherOrgs;
    } else {
      const term = this.filter.toLowerCase();
      const activeOrgMatches = this.activeOrganisationName().toLowerCase().includes(term);
      const filtered = sorted.filter((buid: BUID) =>
        activeOrgMatches ||
        buid.name.toLowerCase().includes(term) ||
        buid.mids.some(m => m.merchantId.toLowerCase().includes(term)) ||
        buid.mids.some(m => m.monekId.toLowerCase().includes(term))
      );
      this.filteredOtherOrgs = otherOrgs
        .filter(org =>
          org.organisationName.toLowerCase().includes(term) ||
          org.businessUnits.some(bu => bu.name.toLowerCase().includes(term))
        )
        .map(org => org.organisationName.toLowerCase().includes(term)
          ? org
          : { ...org, businessUnits: org.businessUnits.filter(bu => bu.name.toLowerCase().includes(term)) }
        );
      this.filteredBuids = (filtered.length > 0 || this.filteredOtherOrgs.length > 0) ? filtered : sorted;
    }
  }

  public hasMultipleOrganisations(): boolean {
    return this.organisations.length > 1;
  }

  public activeOrganisationName(): string {
    return this.organisations.find(o => o.organisationId === this.activeOrganisationId)?.organisationName || '';
  }

  public SelectOtherOrgBuid(org: UserOrganisation, businessUnit?: UserOrganisationBusinessUnit): void {
    if (this.switchingOrgId() !== null) return;
    this.switchingOrgId.set(org.organisationId);
    this.pendingBuidId = businessUnit ? businessUnit.buid : null;
    this.cognitoService.selectOrganisation(org.organisationId, this.pendingBuidId ?? undefined).subscribe((success: boolean) => {
      if (!success) {
        this.switchingOrgId.set(null);
        this.pendingBuidId = null;
        const message = `Unable to switch to '${org.organisationName}'. Please try again later.`;
        this.errorReferenceService.openErrorSnackbar(message, this.cognitoService.lastSelectOrganisationError ?? new Error(message));
      }
    });
  }

  public ToggleDropdown(): void {
    this.dropdownActive = !this.dropdownActive;
    this.filter = '';
    if (this.dropdownActive) {
      this.ngOnInit();
    } else {
      this.setFullscreen(false);
    }
  }

  public ToggleFullscreen(): void {
    this.setFullscreen(!this.fullscreen);
  }

  private setFullscreen(fullscreen: boolean): void {
    this.fullscreen = fullscreen;
    // The picker lives inside mat-sidenav-content, whose stacking context sits
    // below the side nav (mat-drawer). This body class lifts the content pane
    // above the nav while the full-screen view is open (see styles.scss).
    document.body.classList.toggle('merchant-picker-fullscreen', fullscreen);
  }

  public MerchantAvailableMidCount(merchant: BUID): number {
    if (!merchant || !merchant.mids) return 0;
    return merchant.mids.length;
  }

  public SelectMerchant(merchant: BUID): void {
    this.filter = '';
    this.FilterBuids();

    this.selectedBuid.set(merchant); // can still select a buid without a mid - but we should show a warning
    if (!merchant || !merchant.mids || merchant.mids.length === 0) return;
    let primaryMid = merchant.mids.find(m => m.isPrimary && m.status.toUpperCase() === 'ACTIVE');
    if (!primaryMid) primaryMid = merchant.mids.find(m => m.status.toUpperCase() === 'ACTIVE');
    if (!primaryMid) {
      this.snackbar.open(`Now viewing: ${merchant.name}`, 'Close', {
        duration: 1500,
      });
      return;
    }
    this.SelectMerchantMid(primaryMid)
  }

  public SelectMerchantMid(buidMid: BUIDMid): void {
    // cant select a mid without a buid
    const merchant = this.selectedBuid();
    if (merchant=== undefined) return

    this.selectedMid.set(buidMid);
    this.merchantChangeService.setActiveMid(merchant, buidMid);
    this.snackbar.open(`Now viewing: ${merchant.name} ${buidMid.monekId}`, 'Close', {
      duration: 1500,
    });
  }

  public FavouriteToggle(buid: LoadingBuid): void {
    if (buid.favouriteLoading) return; // dont override current action
    buid.favouriteLoading = true;
    if (buid.favourite) {
      this.merchantService.RemoveFavouriteBuid(buid, (updated: boolean, error?: unknown) => {
        if (updated) {
          buid.favourite = false;
        } else {
          const message = `Unable to un-favourite '${buid.name}'. Please try again later.`;
          this.errorReferenceService.openErrorSnackbar(message, error ?? new Error(message));
        }
        buid.favouriteLoading = false;
        this.refreshData();
      });
    } else {
      this.merchantService.AddFavouriteBuid(buid, (updated: boolean, error?: unknown) => {
        if (updated) {
          buid.favourite = true;
        } else {
          const message = `Unable to favourite '${buid.name}'. Please try again later.`;
          this.errorReferenceService.openErrorSnackbar(message, error ?? new Error(message));
        }
        buid.favouriteLoading = false;
        this.refreshData();
      });
    }
  }

  userIsPartner(): boolean {
    return this.cognitoService.isPartner();
  }

  switchOrganisation() {
    this.router.navigate(['/partner']);

    this.ToggleDropdown();
  }
}
